import { useEffect, useState } from "react";
import { mxDaysFromToday } from "../mxDate";
import { useGetIdentity, useNotify } from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { phone10 } from "../contactLinks";
import { isDncAny } from "../dnc/dncRpc";
import { useOffers } from "../useOffers";
import { stageLabel } from "./useOfferStages";
import { useSaveDeal, type DealRow } from "./boardData";
import type { LossTarget } from "./LossDialog";
import { HelpTooltip } from "../help/HelpTooltip";

const fieldCls =
  "h-10 w-full rounded-lg border bg-card px-3 text-sm outline-none focus:border-primary";

/** true si la empresa o alguno de sus contactos está en No contactar */
const companyIsDnc = async (companyId: number) => {
  const sb = getSupabaseClient();
  const [{ data: co }, { data: cts }] = await Promise.all([
    sb.from("companies").select("phone_number").eq("id", companyId).single(),
    sb
      .from("contacts")
      .select("phone_jsonb, email_jsonb")
      .eq("company_id", companyId),
  ]);
  const values = [
    phone10(co?.phone_number),
    ...(cts ?? []).flatMap((c) => [
      ...((c.phone_jsonb ?? []) as { number?: string }[]).map((p) =>
        phone10(p.number),
      ),
      ...((c.email_jsonb ?? []) as { email?: string }[]).map((e) =>
        e.email?.trim().toLowerCase(),
      ),
    ]),
  ].filter((v): v is string => !!v);
  if (!values.length) return false;
  return isDncAny(values);
};

type Pick = { kind: "co" | "dir"; id: number; name: string; where: string };

export type DealFormTarget =
  | { mode: "create"; offerId: number; stage?: string; companyId?: number }
  | { mode: "edit"; deal: DealRow };

/** Crear o editar una oportunidad (PRD B.2), igual que el formulario del prototipo. */
export const DealForm = ({
  target,
  onClose,
  onLose,
  onDelete,
}: {
  target: DealFormTarget | null;
  onClose: () => void;
  onLose: (t: LossTarget) => void;
  onDelete: (deal: DealRow) => void;
}) => {
  const { data: offers } = useOffers();
  const { identity } = useGetIdentity();
  const { save, refresh } = useSaveDeal();
  const notify = useNotify();
  const edit = target?.mode === "edit" ? target.deal : null;

  const [companyText, setCompanyText] = useState("");
  const [picked, setPicked] = useState<Pick | null>(null);
  const [offerId, setOfferId] = useState<number>(0);
  const [stage, setStage] = useState("");
  const [amount, setAmount] = useState("");
  const [next, setNext] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Búsqueda en Empresas y en el directorio (las que aún no están en el CRM)
  const [term, setTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTerm(companyText.trim()), 300);
    return () => clearTimeout(t);
  }, [companyText]);
  const { data: results } = useQuery({
    queryKey: ["deal-company-search", term],
    enabled:
      target?.mode === "create" && term.length >= 2 && term !== picked?.name,
    queryFn: async () => {
      const sb = getSupabaseClient();
      const like = `%${term.replace(/[%_]/g, "")}%`;
      const [{ data: co }, { data: dir }] = await Promise.all([
        sb
          .from("companies")
          .select("id, name, city")
          .ilike("name", like)
          .order("name")
          .limit(8),
        term.length >= 3
          ? sb
              .from("directory")
              .select("id, nombre_comercial, municipio, estado")
              .ilike("nombre_comercial", like)
              .is("company_id", null)
              .eq("dnc", false)
              .order("priority", { ascending: false })
              .limit(8)
          : Promise.resolve({
              data: [] as {
                id: number;
                nombre_comercial: string;
                municipio: string;
                estado: string;
              }[],
            }),
      ]);
      return [
        ...(co ?? []).map(
          (c): Pick => ({
            kind: "co",
            id: c.id,
            name: c.name,
            where: c.city ?? "",
          }),
        ),
        ...(dir ?? []).map(
          (d): Pick => ({
            kind: "dir",
            id: d.id,
            name: d.nombre_comercial,
            where: `${d.municipio}, ${d.estado}`,
          }),
        ),
      ];
    },
  });

  // Cargar valores al abrir
  useEffect(() => {
    if (!target) return;
    if (target.mode === "edit") {
      const d = target.deal;
      setOfferId(d.offer_id);
      setStage(d.stage);
      setAmount(d.amount ? String(d.amount) : "");
      setNext(d.next_step_at ?? "");
      setNote("");
      getSupabaseClient()
        .from("deals")
        .select("description")
        .eq("id", d.id)
        .single()
        .then(({ data }) => setNote(data?.description ?? ""));
    } else {
      const o = offers?.find((x) => x.id === target.offerId);
      setOfferId(target.offerId);
      setStage(target.stage ?? o?.stages[0] ?? "");
      setAmount("");
      setNext(mxDaysFromToday(1));
      setNote("");
      setCompanyText("");
      setPicked(null);
      if (target.companyId)
        getSupabaseClient()
          .from("companies")
          .select("id, name")
          .eq("id", target.companyId)
          .single()
          .then(({ data }) => {
            if (!data) return;
            setPicked({ kind: "co", id: data.id, name: data.name, where: "" });
            setCompanyText(data.name);
          });
    }
  }, [target, offers]);

  if (!target) return null;
  const offer = offers?.find((o) => o.id === offerId);
  // Perder solo por el diálogo de motivo; al crear no se ofrece Perdido
  const stages = (offer?.stages ?? []).filter(
    (s) => target.mode === "edit" || s !== offer?.lost_stage,
  );

  const submit = async () => {
    if (!offer) return;
    const fields = {
      amount: Math.max(0, Number(amount.replace(/\D/g, "")) || 0),
      next_step_at: next || null,
      description: note.trim() || null,
    };
    setSaving(true);
    try {
      if (edit) {
        if (stage === offer.lost_stage && edit.stage !== stage) {
          // guarda lo demás junto con el motivo; si cancela, no cambia nada
          onClose();
          onLose({
            id: edit.id,
            key: edit.key,
            company_name: edit.company_name,
            offer_id: edit.offer_id,
            extra: fields,
          });
          return;
        }
        await save(edit.id, { ...fields, stage });
        notify(`Guardado: ${edit.key}`, { type: "success" });
      } else {
        if (!picked || picked.name !== companyText)
          throw new Error(
            "Elige una empresa de la lista (o créala con + Crear)",
          );
        if (picked.kind === "dir") {
          // Del directorio: pasa al CRM (respeta No contactar) y guarda lo demás
          const { data: dealId, error } = await getSupabaseClient().rpc(
            "promote_directory",
            { p_directory_id: picked.id, p_offer_slug: offer.slug },
          );
          if (error) throw error;
          await save(dealId as number, { ...fields, stage });
          const { data: d } = await getSupabaseClient()
            .from("deals")
            .select("key")
            .eq("id", dealId)
            .single();
          notify(`Creada: ${d?.key}`, { type: "success" });
          onClose();
          setSaving(false);
          return;
        }
        const co = { id: picked.id, name: picked.name };
        if (await companyIsDnc(co.id))
          throw new Error("No se puede: la empresa está en No contactar");
        const sb = getSupabaseClient();
        const { data: cts } = await sb
          .from("contacts")
          .select("id")
          .eq("company_id", co.id)
          .order("id");
        const { data, error } = await sb
          .from("deals")
          .insert({
            ...fields,
            name: `${offer.name} · ${co.name}`,
            company_id: co.id,
            contact_ids: (cts ?? []).map((c) => c.id),
            offer_id: offer.id,
            stage,
            sales_id: identity?.id,
            index: 0,
          })
          .select("key")
          .single();
        if (error?.code === "23505")
          throw new Error(`Ya tiene una oportunidad abierta en ${offer.name}`);
        if (error) throw error;
        refresh();
        notify(`Creada: ${data.key}`, { type: "success" });
      }
      onClose();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1">
            {edit ? `Editar ${edit.key}` : "Nueva oportunidad"}
            {!edit && <HelpTooltip topic="deal.create" />}
          </DialogTitle>
          <DialogDescription>
            {edit ? edit.company_name : "Empresa × producto o servicio"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {!edit && (
            <label className="grid gap-1 text-sm font-semibold sm:col-span-2">
              Empresa
              <Input
                value={companyText}
                onChange={(e) => setCompanyText(e.target.value)}
                placeholder="Busca en Empresas y en el directorio…"
                autoComplete="off"
              />
              {!!results?.length && picked?.name !== companyText && (
                <div className="max-h-56 overflow-y-auto rounded-lg border bg-card font-normal">
                  {results.map((r) => (
                    <button
                      key={`${r.kind}${r.id}`}
                      type="button"
                      className="flex w-full items-center gap-2 border-b px-3 py-2 text-left last:border-0 hover:bg-secondary"
                      onClick={() => {
                        setPicked(r);
                        setCompanyText(r.name);
                      }}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {r.name}
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {r.where}
                        </span>
                      </span>
                      <span className="shrink-0 rounded-full border px-2 text-[11px] font-semibold text-muted-foreground">
                        {r.kind === "co" ? "En el CRM" : "Directorio"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </label>
          )}
          <label className="grid gap-1 text-sm font-semibold">
            <span className="inline-flex items-center gap-1">
              Producto o servicio
              <HelpTooltip topic="deal.offer" />
            </span>
            <select
              className={fieldCls}
              value={offerId}
              disabled={!!edit}
              title={
                edit
                  ? "Para otro producto usa ⋯ › Abrir en otro producto"
                  : undefined
              }
              onChange={(e) => {
                const o = offers?.find((x) => x.id === Number(e.target.value));
                setOfferId(Number(e.target.value));
                setStage(o?.stages[0] ?? "");
              }}
            >
              {offers?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Etapa
            <select
              className={fieldCls}
              value={stage}
              onChange={(e) => setStage(e.target.value)}
            >
              {stages.map((s) => (
                <option key={s} value={s}>
                  {stageLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Valor (MXN)
            <Input
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </label>
          <label className="grid gap-1 text-sm font-semibold">
            Próximo paso
            <Input
              type="date"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </label>
          <label className="grid gap-1 text-sm font-semibold sm:col-span-2">
            Nota
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Opcional"
            />
          </label>
        </div>
        <DialogFooter className="sm:justify-between">
          {edit ? (
            <Button
              variant="ghost"
              className="text-destructive"
              onClick={() => {
                onClose();
                onDelete(edit);
              }}
            >
              Eliminar
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={submit} disabled={saving}>
              {edit ? "Guardar cambios" : "Crear oportunidad"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
